// React and MobX
import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import RemoveIcon from "@material-ui/icons/RemoveCircle";

// Core
import { Loader } from "Core/Components/Loader";
import { useRouter } from "Core/Utils";

// Custom Components
import { Error } from "Custom/Components/Error";
import { DocumentLink } from "Custom/Components/Links/DocumentLink";

// Custom Styles
import { useDocumentsPageStyles } from "Custom/StylesAppSpecific/DashboardSubViews/DocumentPageStyling";
import { DashboardPageStyles, usePageAnimationStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";

// Custom ViewModels
import { DocumentsViewModel } from "Custom/ViewModels/DashboardSubViewModels/DocumentsViewModel";
import { DevelopmentDocumentsViewModel } from "Custom/ViewModels/DashboardSubViewModels/DevelopmentDocumentsViewModel";
import { PropertyDrawingDocumentsViewModel } from "Custom/ViewModels/DashboardSubViewModels/PropertyDrawingDocumentsViewModel";

// Enums and Models
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";
import { DocumentType } from "Custom/Models/Enums/DocumentTypes";
import { DocumentsModel } from "Custom/Models/DashboardModels/DocumentsModel";
import { DashboardViewModel } from "Custom/ViewModels/DashboardViewModel";

export const Documents: React.FC = () => {
    const router = useRouter();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;

    const propertyId = (router.match.params as any).propertyId;

    const dashboardPageStyles = DashboardPageStyles();
    const documentsPageStyles = useDocumentsPageStyles();
    const pageAnimationStyles = usePageAnimationStyles();

    const [viewModel] = useState(() => new DocumentsViewModel(propertyId));
    const [developmentViewModel] = useState(() => new DevelopmentDocumentsViewModel(propertyId));
    const [viewDrawingDocumentModel] = useState(() => new PropertyDrawingDocumentsViewModel(propertyId));

    // Use the shared viewModel
    const [dashboardViewModel] = useState(() => new DashboardViewModel(propertyId, false));

    const [expanded, setExpanded] = useState<string | false>(false);

    const handleAccordionChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const renderBusy = () => <Loader loadingText="Fetching documents..." />;
    const renderError = () => <Error errorText="Failed to retrieve documents information." />;

    const renderPropertyOrDevelopmentCategory = (type: DocumentType, viewModel: any) => {
        return (
            <Box className={`${documentsPageStyles.documentCategories} ${!isDesktop && documentsPageStyles.isMobileDocumentCategories}`}>
                {viewModel.model.documentCategories && viewModel.model.documentCategories.length > 0 ? (
                    viewModel.model.documentCategories.map((category: DocumentsModel, index: number) => (
                        <Box
                            key={`category-${index}`}
                            className={`${documentsPageStyles.categoryContainer} ${
                                index === viewModel.model.documentCategories.length - 1 ? documentsPageStyles.categoryContainerLast : ""
                            }`}
                        >
                            <Typography variant="h6" className={documentsPageStyles.categoryName}>
                                {category.documentTitle}
                            </Typography>
                            <Box className={documentsPageStyles.gridContainer}>
                                {category.documents && category.documents.length > 0 ? (
                                    category.documents.map(
                                        (doc, docIndex) =>
                                            doc &&
                                            doc.documentUrl && (
                                                <Box
                                                    key={`document-${docIndex}`}
                                                    className={`${documentsPageStyles.gridContainer} ${!isDesktop && documentsPageStyles.isMobiledocumentItem}`}
                                                >
                                                    <DocumentLink
                                                        className={documentsPageStyles.documentLink}
                                                        displayName={doc.documentName}
                                                        link={doc.documentUrl}
                                                        id={doc.id}
                                                        type={type}
                                                        viewModel={viewModel}
                                                    />
                                                </Box>
                                            ),
                                    )
                                ) : (
                                    <Typography variant="body2">No documents available for this category.</Typography>
                                )}
                            </Box>
                        </Box>
                    ))
                ) : (
                    <Typography>No document categories available.</Typography>
                )}
            </Box>
        );
    };

    const renderDrawingCategories = () => {
        return (
            <Box className={`${documentsPageStyles.documentCategories} ${!isDesktop && documentsPageStyles.isMobileDocumentCategories}`}>
                {viewDrawingDocumentModel.Documents && viewDrawingDocumentModel.Documents.length > 0 ? (
                    viewDrawingDocumentModel.Documents.map((doc, index) => (
                        <Box
                            key={`category-${index}`}
                            className={`${documentsPageStyles.categoryContainer} ${
                                index === viewDrawingDocumentModel.Documents.length - 1 ? documentsPageStyles.categoryContainerLast : ""
                            }`}
                        >
                            <Typography variant="h6" className={documentsPageStyles.categoryName}>
                                {doc.DocumentName.split(/(?=[A-Z])/).join(" ")}
                            </Typography>
                            {doc && doc.documentUrl && (
                                <Box key={`document-${index}`} className={`${documentsPageStyles.gridContainer} ${!isDesktop && documentsPageStyles.isMobiledocumentItem}`}>
                                    <DocumentLink
                                        className={documentsPageStyles.documentLink}
                                        displayName={doc.fileName}
                                        link={doc.documentUrl}
                                        id={doc.id}
                                        type={DocumentType.drawing}
                                        viewModel={viewDrawingDocumentModel}
                                    />
                                </Box>
                            )}
                        </Box>
                    ))
                ) : (
                    <Typography>No drawing documents available.</Typography>
                )}
            </Box>
        );
    };

    const renderDocumentSection = (title: string, viewModel: DocumentsViewModel | PropertyDrawingDocumentsViewModel | DevelopmentDocumentsViewModel, type: DocumentType) => {
        const panel = title.replace(" ", "").toLowerCase();

        return (
            <Box style={{ paddingBottom: "0px" }} className={dashboardPageStyles.workspace}>
                <Accordion expanded={expanded === panel} onChange={handleAccordionChange(panel)}>
                    <AccordionSummary
                        className={`${documentsPageStyles.accordionHeader} ${!isDesktop && documentsPageStyles.isMobileaccordionHeader}`}
                        style={{ display: "flex", alignItems: "center" }}
                        aria-controls="panel-content"
                        id="panel-header"
                    >
                        {expanded === panel ? <RemoveIcon style={{ marginRight: 10 }} /> : <AddCircleIcon style={{ marginRight: 10 }} />}
                        <Typography className={documentsPageStyles.accordionTitle} variant="h5">{`${title} (${viewModel.DocumentsLength})`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={documentsPageStyles.accordionDetails}>
                        {title.includes("Drawing") ? renderDrawingCategories() : renderPropertyOrDevelopmentCategory(type, viewModel)}
                    </AccordionDetails>
                </Accordion>
            </Box>
        );
    };

    const renderItems = () => {
        return (
            <Box className={`${documentsPageStyles.documentPage} ${!isDesktop && documentsPageStyles.ismobileViewAccordionBlock}`}>
                <Typography className={`baseHeader ${documentsPageStyles.documentHeader}`} variant="h5">
                    Your documents
                    <Typography component="span" style={{ fontSize: "15px", lineHeight: "25px" }}>
                        {`(${dashboardViewModel.dashboardDetail.propertyAddress})`}
                    </Typography>
                </Typography>
                <Box style={{ padding: "12px 20px" }}>
                    {/* Property Documents Section */}
                    {renderDocumentSection("Property Documents", viewModel, DocumentType.property)}

                    {/* Development Documents Section */}
                    {renderDocumentSection("Development Documents", developmentViewModel, DocumentType.development)}

                    {/* Drawing Documents Section */}
                    {renderDocumentSection("Drawing Documents", viewDrawingDocumentModel, DocumentType.drawing)}
                </Box>
            </Box>
        );
    };

    return useObserver(() => (
        <Box style={{ justifyContent: "unset" }} className={`${dashboardPageStyles.root}  ${documentsPageStyles.root} ${pageAnimationStyles.pageWrapper}`}>
            <Box style={{ height: "100%" }} className={`${dashboardPageStyles.workspace} ${!isDesktop && documentsPageStyles.isMobileworkspace}`}>
                {viewModel.ApiStatus === ApiStatus.success && <React.Fragment>{renderItems()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.loading && <React.Fragment>{renderBusy()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.error && <React.Fragment>{renderError()}</React.Fragment>}
            </Box>
        </Box>
    ));
};
